"use client";
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { UserRepository } from "./repositories/user-repository";
import { ClientUser } from "./types/user";

import { Result, Button, Empty, message } from "antd";
import { ReactNode } from "react";
import { Spin } from "@/ui/spin";
import { getIsAuthorized, LoginModal, unAuth } from "@/components/login-modal/login-modal";
import { useRouter } from "next/navigation";
import { useMessage } from "@/app/messageContext";
import { AxiosError } from "axios";
import { queryClient } from "./http-client/base-client";

export const useUser = () =>
  useQuery<ClientUser, AxiosError>({
    queryKey: ["user-info"],
    queryFn: UserRepository.fetchUser,
    staleTime: 600_000, // 1 минута до инвалидации
    refetchOnWindowFocus: false,
    retry: false
  });

export const usePatchUser = () => {
  const messageApi = useMessage();

  return useMutation({
    mutationFn: UserRepository.patchUser,
    onSuccess: () => {
      messageApi.open({ type: "success", content: "Информация обновлена" });
    },
    onError: () => {
      messageApi.open({ type: "error", content: "Не удалось обновить" });
    },
  });
};

export const usePostPhone = (onSuccess: () => void) => {
  const messageApi = useMessage();

  return useMutation({
    mutationFn: UserRepository.postPhone,
    onSuccess,
    onError: () => {
      messageApi.open({ type: "error", content: "Не удалось отправить sms" });
    },
  });
};
export const usePostPhoneCheck = (
  onSuccess: () => void,
  onError: () => void,
) => {
  const messageApi = useMessage();

  return useMutation({
    mutationFn: UserRepository.postPhoneCheck,
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["user-info"] });
      onSuccess();
      messageApi.success("Телефон успешно подтверждён");
    },
    onError: (error) => {
      onError();
      messageApi.error(error.message);
    },
  });
};

export const useDeleteUser = (onSuccess?: () => void) => {
  const messageApi = useMessage();

  return useMutation({
    mutationFn: UserRepository.deleteUser,
    onSuccess: () => {
      onSuccess?.();
      messageApi.open({ type: "success", content: "Аккаунт удален" });
    },
    onError: () => {
      messageApi.open({ type: "error", content: "Не удалось удалить." });
    },
  });
};

export type UserProps = {
  user: ClientUser;
};

type UserStateProps = {
  isLoading: boolean;
  error: AxiosError | null;
  isEmpty?: boolean;
  loadingComponent?: ReactNode;
  errorComponent?: ReactNode;
  emptyComponent?: ReactNode;
  children: ReactNode;
  refetch: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<ClientUser, Error>>;
};

export const UserState = ({
  isLoading,
  error,
  isEmpty = false,
  loadingComponent,
  errorComponent,
  emptyComponent,
  children,
}: UserStateProps) => {
  const router = useRouter();

  if (isLoading) {
    return (
      loadingComponent ?? (
        <div style={{ height: "100vh" }}>
          <Spin fullscreen />
        </div>
      )
    );
  }

  if (error) {
    if ((error.status === 401 || !getIsAuthorized())) {
      unAuth();
      return (
        <div style={{ height: "100vh" }}>
          <LoginModal
            isOpen={true}
            setIsOpen={() => {
              router.back();
            }}
            onLogin={() =>
              queryClient.invalidateQueries({ queryKey: ["user-info"] })
            }
          />
        </div>
      );
    }

    return (
      errorComponent ?? (
        <div style={{ height: "100vh" }}>
          <Result
            status="error"
            title="Ошибка. Попробуйте позднее."
            subTitle={error.message}
            extra={
              <Button onClick={() => window.location.reload()}>Refresh</Button>
            }
          />
        </div>
      )
    );
  }

  if (isEmpty) {
    return (
      emptyComponent ?? (
        <div style={{ height: "100vh" }}>
          <Result
            status="warning"
            title="Ошибка. Пользователь не найден."
            extra={
              <Button onClick={() => window.location.reload()}>Refresh</Button>
            }
          />
        </div>
      )
    );
  }

  return <>{children}</>;
};

export const withUser = <P extends UserProps>(
  WrappedComponent: React.ComponentType<P>,
  options?: {
    loadingComponent?: ReactNode;
    errorComponent?: ReactNode;
    emptyComponent?: ReactNode;
    requireAuth?: boolean; // Добавляем опцию для обязательной аутентификации
  },
) => {
  return (props: Omit<P, keyof UserProps>) => {
    const { data: user, isLoading, error, refetch } = useUser();

    return (
      <UserState
        isLoading={isLoading}
        error={error}
        isEmpty={!user}
        loadingComponent={options?.loadingComponent}
        errorComponent={options?.errorComponent}
        emptyComponent={options?.emptyComponent}
        refetch={refetch}
      >
        <WrappedComponent {...(props as P)} user={user!} />
      </UserState>
    );
  };
};
